import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { seedDatabase } from '@/db/seed';
import AppLayout from '@/components/feature/AppLayout';
import { canManageMasterData } from '@/auth/access';
import { useIdleLogout } from '@/hooks/useIdleLogout';

export default function AppRoot() {
  const { user, isLoading, logout } = useAuth();
  const { secondsLeft, stayConnected } = useIdleLogout(!!user, !isLoading, logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [showCoffeeTransition, setShowCoffeeTransition] = useState(false);

  useEffect(() => {
    let timer: number | undefined;
    const showTransition = () => {
      sessionStorage.removeItem('zsb_show_coffee_transition');
      setShowCoffeeTransition(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setShowCoffeeTransition(false), 1550);
    };
    if (sessionStorage.getItem('zsb_show_coffee_transition') === '1') showTransition();
    window.addEventListener('zsb-login-success', showTransition);
    return () => {
      window.removeEventListener('zsb-login-success', showTransition);
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    seedDatabase().catch(() => {});
  }, []);

  useEffect(() => {
    if (!isLoading && !user && location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }
    if (!isLoading && user && location.pathname === '/login') {
      navigate(user.forcePasswordChange ? '/alterar-senha' : '/', { replace: true });
    }
    if (!isLoading && user?.forcePasswordChange && location.pathname !== '/alterar-senha') {
      navigate('/alterar-senha', { replace: true });
    }
    if (!isLoading && user && !user.forcePasswordChange && location.pathname === '/alterar-senha') {
      navigate('/', { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-50">
        <div className="animate-pulse text-primary-500 font-heading text-lg">Carregando...</div>
      </div>
    );
  }

  // Render guards prevent mounting protected pages before redirect effects run.
  if (!user && location.pathname !== '/login') return <Navigate to="/login" replace />;
  if (user?.forcePasswordChange && location.pathname !== '/alterar-senha') return <Navigate to="/alterar-senha" replace />;
  if (['/clientes', '/produtos', '/tabelas'].includes(location.pathname.replace(/\/$/, '')) && !canManageMasterData(user)) return <Navigate to="/" replace />;
  if (!user || location.pathname === '/alterar-senha') {
    return <div key={location.pathname} className="route-page-enter min-h-screen"><Outlet /></div>;
  }

  return (
    <>
    <AppLayout key={user.id}>
        <Outlet />
      </AppLayout>
      {secondsLeft !== null && <div className="fixed inset-0 z-[200] flex items-center justify-center bg-primary-950/60 p-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="idle-title" aria-describedby="idle-desc">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-2xl text-amber-700"><i className="ri-time-line" aria-hidden="true" /></span>
          <h2 id="idle-title" className="mt-4 font-heading text-lg font-bold text-foreground-950">Você ainda está aí?</h2>
          <p id="idle-desc" className="mt-2 text-sm text-foreground-600">Por segurança, sua sessão será encerrada por inatividade em <strong>{secondsLeft}s</strong>. Se estiver montando uma cotação, salve antes para não perder o trabalho.</p>
          <button type="button" autoFocus onClick={stayConnected} className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700">Continuar conectado</button>
        </div>
      </div>}
      {showCoffeeTransition && <div className="coffee-transition" role="status" aria-live="polite" aria-label="Preparando seu ambiente de trabalho">
        <div className="coffee-transition__glow" />
        <div className="coffee-transition__content">
          <div className="coffee-transition__steam" aria-hidden="true"><span /><span /><span /></div>
          <div className="coffee-transition__cup" aria-hidden="true"><i className="ri-cup-line" /></div>
          <p className="coffee-transition__eyebrow">Acesso autorizado</p>
          <h2>Preparando seu ambiente...</h2>
          <p>Um café rápido enquanto organizamos seus indicadores.</p>
          <div className="coffee-transition__progress"><span /></div>
        </div>
      </div>}
    </>
  );
}
